import type { AuditId } from "@/packages/types";
import { ActivityFeed } from "@/components/ui";
import { useTranslation } from "@/packages/utils";
import { useAuditActivities } from "@/hooks/use-audit-activities";
import { useSearch, useNavigate } from "@tanstack/react-router";

interface AuditActivityTabProps {
  auditId: AuditId;
}

export function AuditActivityTab({ auditId }: AuditActivityTabProps) {
  const { t } = useTranslation(["audits"]);
  const { activities, isLoading, fieldLabels, eventTypeRenderers } =
    useAuditActivities(auditId);
  const search = useSearch({ from: "/$workspaceSlug/audits/$auditId" });
  const navigate = useNavigate({ from: "/$workspaceSlug/audits/$auditId" });

  // Only enable URL persistence when on the activity tab
  const isActivityTab = search.tab === "activity";

  // Handle URL updates for filters and search
  const handleUrlUpdate = (params: {
    categories?: string;
    search?: string;
  }) => {
    navigate({
      search: (prev: Record<string, unknown>) => ({
        ...prev,
        categories: params.categories,
        search: params.search,
      }),
      replace: true,
    });
  };

  if (isLoading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 rounded-full border-2 border-accent border-t-transparent animate-spin" />
          <p className="text-xs text-muted">{t("audits:detail.loading")}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col min-h-0">
      <ActivityFeed
        items={activities}
        emptyTitle={t("audits:detail.activity.emptyTitle")}
        emptyDescription={t("audits:detail.activity.emptyDescription")}
        persistFiltersToUrl={isActivityTab}
        urlSearchParams={{
          categories: search.categories as string | undefined,
          search: search.search as string | undefined,
        }}
        onUrlUpdate={isActivityTab ? handleUrlUpdate : undefined}
        fieldLabels={fieldLabels}
        eventTypeRenderers={eventTypeRenderers}
      />
    </div>
  );
}
